import React from 'react';

import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { loadApplication } from '../../action/application/load-job-application';
import { setApplicationActivityDirty } from '../../action/set-application-activity-dirty';
import { setContactMessagesDirty } from '../../contacts/actions/set-contact-messages-dirty';
import { loadJob } from '../../job/actions/load-job';
import { loadJobApplicationCountsByStatus } from '../../job/actions/load-job-application-counts-by-status';
import { loadOrgApplicationCountsByStatus } from '../../job/actions/load-org-application-counts-by-status';
import { setJobApplicationCountsByStatusDirty } from '../../job/actions/set-job-application-counts-by-status-dirty';
import { setOrgApplicationCountsByStatusDirty } from '../../job/actions/set-org-application-counts-by-status-dirty';
import { loadJobMessages } from '../../messages/actions/load-job-messages';
import { setJobApplicantJobMessagesDirty } from '../../messages/actions/set-job-applicant-job-messages-dirty';
import { setOrgJobApplicantMessagesDirty } from '../../messages/actions/set-org-job-applicant-messages-dirty';
import { updateJobApplicantMessage } from '../../messages/actions/update-job-applicant-message';
import { fullApplicationSelector } from '../../reducers/application-selectors';
import { jobSelector } from '../../reducers/job-selectors';

import { jobMessagesRoute } from '../../web-routes';

import { IncomingMessageNotification } from './incoming-message-notification';

import { formatLocation } from '../../util/formatters';

const mapStateToProps = (state: any, { jobApplicantId }: InputProps) => {
  const jobApplicant = fullApplicationSelector(state, jobApplicantId);

  return {
    jobApplicant,
    job: jobApplicant ? jobSelector(state, jobApplicant.job_id) : null,
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { jobApplicantId, message }: InputProps,
) => ({
  actions: {
    loadJob: (jobId: number) => dispatch(loadJob(jobId)),
    loadJobApplicant: () => dispatch(loadApplication(jobApplicantId)),
    loadJobMessages: (jobId: number) => dispatch(loadJobMessages(jobId)),
    markMessageRead: (contactId: number) =>
      dispatch(
        updateJobApplicantMessage(contactId, message.messageId, jobApplicantId, {
          isUnread: false,
        }),
      ),
  },
});

type InputProps = {
  jobApplicantId: number;
  message: Message;
  onClose: () => void;
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps & RouteComponentProps;

export const prepareApplicantMessageNotificationData = async (
  jobApplicantId: number,
  store: any,
) => {
  await store.dispatch(loadApplication(jobApplicantId));
  const jobApplicant = fullApplicationSelector(store.getState(), jobApplicantId);

  // IF (01/27/21): If an applicant sends in a quick series of texts or our user has network issues,
  // this load can fail. In that case we just bail out and don't show a notification.
  if (!jobApplicant) {
    return;
  }
  // Set states dirty
  await Promise.allSettled([
    store.dispatch(setJobApplicationCountsByStatusDirty(jobApplicant.job_id)),
    store.dispatch(setOrgApplicationCountsByStatusDirty(jobApplicant.orgId)),
    store.dispatch(setContactMessagesDirty(jobApplicant.contactId)),
    store.dispatch(setJobApplicantJobMessagesDirty(jobApplicant.job_id)),
    store.dispatch(setOrgJobApplicantMessagesDirty(jobApplicant.orgId)),
  ]);

  // Reload data
  await Promise.allSettled([
    store.dispatch(loadJob(jobApplicant.job_id)),
    store.dispatch(loadJobMessages(jobApplicant.job_id)),
    store.dispatch(setApplicationActivityDirty(jobApplicantId)),
    store.dispatch(loadJobApplicationCountsByStatus(jobApplicant.job_id)),
    store.dispatch(loadOrgApplicationCountsByStatus(jobApplicant.orgId)),
  ]);
};

class ApplicantMessageNotificationComponent extends React.PureComponent<Props> {
  componentDidMount() {
    const { actions, jobApplicant } = this.props;
    actions.loadJobApplicant();
    if (jobApplicant) {
      actions.loadJob(jobApplicant.job_id);
    }
  }

  componentDidUpdate() {
    const { actions, jobApplicant } = this.props;
    actions.loadJobApplicant();
    if (jobApplicant) {
      actions.loadJob(jobApplicant.job_id);
    }
  }

  handleViewClick = () => {
    const { history, job, jobApplicantId, onClose } = this.props;
    if (job) {
      history.push(jobMessagesRoute(job.orgId, job.job_id, jobApplicantId));
    }
    onClose();
  };

  handleMarkReadClick = async () => {
    const {
      actions,
      onClose,
      jobApplicant: { contactId, job_id: jobId },
    } = this.props;
    await actions.markMessageRead(contactId);
    actions.loadJobMessages(jobId);
    onClose();
  };

  render() {
    const { jobApplicant, message, onClose, job } = this.props;

    if (!jobApplicant || !job) {
      return null;
    }

    // IF (01/22/20): If the user is viewing the message thread, don't show a notification
    if (window.location.pathname === jobMessagesRoute(job.orgId, job.job_id)) {
      return null;
    }

    const { city, state } = job;
    const location = formatLocation({ city, state });

    return (
      <IncomingMessageNotification
        messageType={message.type}
        senderName={`${jobApplicant.firstName} ${jobApplicant.lastName}`}
        messageBody={message.messageBody}
        subtitle={`${job.title}${location ? ` - ${location}` : ''}`}
        onClose={onClose}
        onView={this.handleViewClick}
        onMarkAsRead={this.handleMarkReadClick}
      />
    );
  }
}

export const ApplicantMessageNotification = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ApplicantMessageNotificationComponent),
);
